import { useState, useRef, useEffect, useCallback } from 'react';
import { resolveMediaUrl } from '../services/api';
import { toast } from '../utils/toastBus';

export function useAudioPlayer() {
  const audioPlayerRef = useRef(null);
  const [activeAudio, setActiveAudio] = useState(null);
  const [audioPlayerStatus, setAudioPlayerStatus] = useState('idle');

  const stopAudio = useCallback(() => {
    const el = audioPlayerRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setAudioPlayerStatus('idle');
    setActiveAudio(null);
  }, []);

  const playAudio = useCallback((asset, idx, origin = 'library') => {
    const rawUrl = asset?.url || asset?.audioPath || '';
    const resolvedUrl = resolveMediaUrl(rawUrl);
    if (!resolvedUrl) {
      try {
        toast({ type: 'error', message: 'Audio unavailable' });
      } catch {}
      return;
    }

    const keyBase = asset?.id || asset?.assetId || asset?._id || `${origin}-${idx}`;
    const cardKey = `${origin}:${keyBase}`;
    const label = asset?.name || asset?.filename || 
      (resolvedUrl ? decodeURIComponent(resolvedUrl.split('/').pop() || '') : `Audio ${idx + 1}`);

    const el = audioPlayerRef.current;
    if (activeAudio?.key === cardKey && el) {
      if (audioPlayerStatus === 'playing') {
        el.pause();
        setAudioPlayerStatus('paused');
      } else {
        const resume = el.play();
        if (resume?.catch) resume.catch(() => {});
        setAudioPlayerStatus('loading');
      }
      return;
    }

    setAudioPlayerStatus('loading');
    setActiveAudio({ key: cardKey, src: resolvedUrl, label });
  }, [activeAudio, audioPlayerStatus]);

  useEffect(() => {
    const el = audioPlayerRef.current;
    if (!el) return;

    const handlePlay = () => setAudioPlayerStatus('playing');
    const handlePause = () => setAudioPlayerStatus(prev => (prev === 'idle' ? prev : 'paused'));
    const handleEnded = () => setAudioPlayerStatus('ended');
    const handleError = () => setAudioPlayerStatus('error');

    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);
    el.addEventListener('error', handleError);

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
      el.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    const el = audioPlayerRef.current;
    if (!el) return;

    if (activeAudio?.src) {
      if (el.src !== activeAudio.src) {
        el.src = activeAudio.src;
      }
      const playPromise = el.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else {
      el.pause();
      el.removeAttribute('src');
      el.load();
      setAudioPlayerStatus('idle');
    }
  }, [activeAudio]);

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  return {
    audioPlayerRef,
    activeAudio,
    audioPlayerStatus,
    playAudio,
    stopAudio
  };
}
